import type { OperationVariables } from '@apollo/client'
import { skipToken, type SkipToken, type useQuery, type useSubscription } from '@apollo/client/react'
import { isFunction } from 'lodash-es'

type Truthy<T> = Exclude<T, null | undefined | false | 0 | ''>

type QueryOptions = useQuery.Options<unknown, OperationVariables>
type SubscriptionOptions = useSubscription.Options<unknown, OperationVariables>
type HookOptions = QueryOptions | SubscriptionOptions

type AcceptedKey = Exclude<keyof QueryOptions | keyof SubscriptionOptions, 'skip'>

/** Rejects `skip` and keys no hook accepts, which a generic return type would otherwise let through. */
type NoUnknownOptions<TOptions> = TOptions & Record<Exclude<keyof TOptions, AcceptedKey>, never>

/**
 * Parks a query or subscription on `skipToken` until `value` is truthy. A query parked since mount is
 * not refetched by name and has no variables, so a `refetch()` it exposes must be guarded by the same
 * condition.
 */
export const skipTokenUnless = <TValue, TOptions extends HookOptions>(
  value: TValue,
  /** Options, or a builder that receives `value` narrowed. */
  optionsOrBuild: ((value: Truthy<TValue>) => NoUnknownOptions<TOptions>) | NoUnknownOptions<TOptions>,
): TOptions | SkipToken => {
  if (!value) {
    return skipToken
  }

  return isFunction(optionsOrBuild) ? optionsOrBuild(value as Truthy<TValue>) : optionsOrBuild
}
